import React, { FC, useState } from 'react';
import { Button } from 'antd';

import Modal from '../Modal/Modal';
import api from '../../utils/api.util';
import { IOrderDetails } from '../../interfaces/common';
import { ENDPOINT } from '../../constants/common-app.const';

import css from './PaymentModal.module.css';
import { SVGNewTab } from './OpenInNewTab';
import Spinner from '../Spinner/Spinner';

interface IErip {
  accountNumber: string;
  invoiceNumber: number;
  amountToPay: number;
  eripQRCodeLink: string;
}

export interface IPaymentModal {
  onClose: () => void;
  isDisplay: boolean;
  tariffId?: string;
  themeId?: string;
  paymentData: string[];
}

const PaymentModal: FC<IPaymentModal> = ({
  onClose,
  isDisplay,
  paymentData,
}) => {
  const [themeId, tariffId] = paymentData;
  const [isLoading, setLoading] = useState<boolean>(false);
  const [eripCode, setEripCode] = useState<IErip>();

  const handlePay = async (erip?: boolean) => {
    const dataForOrder = {
      themeId,
      tariff: tariffId,
      erip,
    };

    setLoading(true);

    try {
      const orderDetails: IOrderDetails = await api.post(
        ENDPOINT.ORDERS,
        dataForOrder,
      );

      if (erip) {
        setEripCode({
          accountNumber: orderDetails.eripAccountNumber,
          invoiceNumber: orderDetails.eripInvoiceNumber,
          amountToPay: orderDetails.amountToPay,
          eripQRCodeLink: orderDetails.eripQRCodeLink,
        });

        setLoading(false);
        return;
      }

      // window.open(`${orderDetails.formUrl}`, '_blank');
      // window.location.reload();

      window.location.href = orderDetails.formUrl;
    } catch (err: any) {
      window.location.reload();
    }

    setLoading(false);
  };

  const handleClose = () => {
    setEripCode(undefined);
    onClose();
  };

  return (
    <Modal
      onClose={handleClose}
      display={isDisplay}
      title={
        eripCode
          ? `Оплатите счёт: ${eripCode.invoiceNumber}`
          : 'Выберите способ оплаты'
      }
    >
      {eripCode ? (
        <EripContent
          eripCode={eripCode}
          onClose={() => setEripCode(undefined)}
          onSuccess={() => window.location.reload()}
        />
      ) : (
        <PaymentContent isLoading={isLoading} onPay={handlePay} />
      )}
    </Modal>
  );
};

interface PaymentContentProps {
  isLoading: boolean;
  onPay: (erip: boolean) => void;
}

const PaymentContent: FC<PaymentContentProps> = ({ isLoading, onPay }) => {
  const [loadingType, setLoadingType] = useState<'erip' | 'card'>();

  const handlePay = (erip: boolean) => {
    setLoadingType(erip ? 'erip' : 'card');
    onPay(erip);
  };

  return (
    <div className={css.Container}>
      <div className={css.Wrapper}>
        <Button
          onClick={() => handlePay(false)}
          className={css.Button}
          type='primary'
          disabled={isLoading}
        >
          {isLoading && loadingType === 'card' ? <Spinner /> : 'Оплата картой'}
        </Button>

        <Button
          className={css.Button}
          onClick={() => window.open('https://forms.amocrm.ru/rvzdlmv')}
          disabled={isLoading}
        >
          Банковский перевод
        </Button>

        <Button
          className={css.Button}
          onClick={() => handlePay(true)}
          disabled={isLoading}
        >
          {isLoading && loadingType === 'erip' ? (
            <Spinner />
          ) : (
            'Оплата через ЕРИП'
          )}
        </Button>
      </div>
    </div>
  );
};

interface EripProps {
  eripCode: IErip;
  onClose: () => void;
  onSuccess: () => void;
}

const EripContent: FC<EripProps> = ({ eripCode, onClose, onSuccess }) => {
  const now = new Date();
  now.setHours(now.getHours() + 1);

  const formattedTime = now.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <div className={css.Erip}>
      <h3 className={css.Title}>
        Вам необходимо произвести платеж в любой системе, позволяющей проводить
        оплату через ЕРИП (пункты банковского обслуживания, банкоматы, платежные
        терминалы, системы интернет-банкинга, клиент-банкинга и т.п.).
        <br /> <br />В перечне услуг ЕРИП перейдите в раздел:
      </h3>
      <ul className={css.List}>
        <li>
          Образование и развитие → Дополнительное образование и развитие →
          Профессиональное образование → Гродненская обл. → Регион. аген-во
          безоп-ти труда → Тестирование <br />
          Можно осуществить поиск по коду услуги: <b>256125</b>
        </li>
        <li>
          В поле «Номер счета» введите <b>{eripCode.accountNumber}</b> и нажмите
          «Продолжить»
        </li>
        <li>
          Сумма для оплаты <b>{eripCode.amountToPay} BYN</b>
        </li>
      </ul>

      <div className={css.Info}>
        <p>
          Дата действия счета: <b>{formattedTime}</b>
        </p>
        <br />
        <p>
          Посмотреть счет и детальные инструкции по способам оплаты{' '}
          <a
            href={eripCode.eripQRCodeLink}
            target='_blank'
            rel='noreferrer'
            className={css.Link}
          >
            можно по ссылке
            <SVGNewTab />
          </a>
        </p>
      </div>

      <div className={css.Actions}>
        <Button onClick={onClose}>Отмена</Button>
        <Button type='primary' onClick={onSuccess}>
          Я оплатил
        </Button>
      </div>
    </div>
  );
};

export default PaymentModal;
